/**
 * Program Detail Page - Individual program/course page
 * /programs/:slug
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { usePageContent, getSection } from '@/hooks/usePageContent';
import SEO from '@/components/SEO';
import InfoRow from '@/components/InfoRow';
import MBBSLeadForm from '@/components/programs/MBBSLeadForm';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  GraduationCap,
  MapPin,
  Clock,
  DollarSign,
  Calendar,
  CheckCircle2,
  Briefcase,
  Building2,
  TrendingUp,
  Award,
  FileText,
  Globe,
  ArrowLeft,
  ChevronRight,
  Phone,
  MessageCircle,
  Loader2,
  BookOpen,
  Users,
  Plane
} from 'lucide-react';

const API_URL = window.location.origin || '';

const ProgramDetailPage = () => {
  const { slug } = useParams();
  const { isDark } = useTheme();
  const [program, setProgram] = useState(null);
  const [relatedPrograms, setRelatedPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // CMS page-template strings (Phase C wiring)
  const { content: cmsContent } = usePageContent('programs-detail');
  const t = getSection(cmsContent, 'template_strings', {
    apply_now: 'Book Free Consultation',
    requirements_header: 'Eligibility Requirements',
    tuition_header: 'Tuition Fees',
    duration_label: 'Duration',
    intake_label: 'Intake',
    related_header: 'Related Programs'
  });
  const detailFinalCta = getSection(cmsContent, 'final_cta', {
    title: null,  // null → use program-specific copy
    cta_primary_text: 'Book Free Consultation',
    cta_secondary_text: 'Talk to AI Counselor'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setNotFound(false);
        // Fetch this program
        const res = await fetch(`${API_URL}/api/programs/public/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setProgram(data);

          // Fetch related programs (same destination or field)
          const relatedRes = await fetch(`${API_URL}/api/programs/public`);
          if (relatedRes.ok) {
            const allPrograms = await relatedRes.json();
            const related = allPrograms
              .filter(p => p.slug !== slug && (p.destination === data.destination || p.field_of_study === data.field_of_study))
              .slice(0, 3);
            setRelatedPrograms(related);
          }
        } else {
          // Program does not exist (404 etc.) — show a proper not-found state
          // rather than silently redirecting (which lands users on an empty list
          // if that fetch also fails and confuses them).
          setNotFound(true);
        }
      } catch (error) {
        console.error('Error fetching program:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className={`min-h-screen pt-20 flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-ux-blue" />
      </div>
    );
  }

  if (notFound || !program) {
    return (
      <div className={`min-h-screen pt-20 flex items-center justify-center px-4 ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`} data-testid="program-not-found">
        <div className="text-center max-w-md">
          <BookOpen className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-white/20' : 'text-slate-300'}`} />
          <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Program not found
          </h1>
          <p className={`mb-6 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
            The program you&apos;re looking for isn&apos;t available. Browse our full list of study abroad programs instead.
          </p>
          <Button asChild data-testid="back-to-programs-button">
            <Link to="/programs">
              <ArrowLeft className="w-4 h-4 mr-2" /> View all programs
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  // Schema.org Course structured data
  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": `${program.title} in ${program.destination}`,
    "description": program.description,
    "provider": {
      "@type": "Organization",
      "name": "UniXperts",
      "url": "https://unixperts.com"
    },
    "educationalLevel": program.degree_level,
    "about": program.field_of_study,
    "inLanguage": "en",
    "isAccessibleForFree": false,
    "offers": {
      "@type": "Offer",
      "price": program.avg_tuition,
      "priceCurrency": program.destination === "USA" ? "USD" : program.destination === "UK" ? "GBP" : program.destination === "Canada" ? "CAD" : program.destination === "Australia" ? "AUD" : "EUR"
    }
  };

  const eligibility = program.eligibility || {};
  const careerProspects = program.career_prospects || {};

  return (
    <>
      {/* SEO Meta Tags */}
      <SEO 
        title={`${program.title} in ${program.destination} - Eligibility, Fees, Universities`}
        description={program.description}
        keywords={program.keywords}
        canonical={`https://unixperts.com/programs/${program.slug || program._id}`}
      />
      
      {/* Inject Course Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(courseSchema) }} />

      <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
        {/* Hero Section */}
        <section className={`py-12 md:py-20 ${isDark ? 'bg-gradient-to-br from-[#1a2830] to-[#0d1117]' : 'bg-gradient-to-br from-ux-blue to-ux-cyan'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Link */}
            <Link
              to="/programs"
              className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
              data-testid="back-to-programs"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              All Programs
            </Link>

            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Badge className="bg-white/20 text-white">
                    {program.destination}
                  </Badge>
                  <Badge className="bg-white/20 text-white">
                    {program.degree_level}
                  </Badge>
                  <Badge className="bg-white/20 text-white">
                    {program.field_of_study}
                  </Badge>
                </div>

                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 font-display" data-testid="program-title">
                  {program.title.toLowerCase().includes(` in ${(program.destination || '').toLowerCase()}`)
                    ? program.title
                    : `${program.title} in ${program.destination}`}
                </h1>
                <p className="text-xl text-white/80 mb-6">
                  {program.tagline}
                </p>

                <div className="flex flex-wrap gap-6 text-white/90">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    <span>{program.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    <span>{program.avg_tuition}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    <span>Intake: {program.intake_seasons?.join(', ')}</span>
                  </div>
                </div>
              </div>

              {/* Quick CTA Card */}
              <div className={`p-6 rounded-2xl ${isDark ? 'bg-white/10 backdrop-blur-sm' : 'bg-white/20 backdrop-blur-sm'}`}>
                <h2 className="text-xl font-bold text-white mb-4">Start Your Application</h2>
                <p className="text-white/80 mb-6 text-sm">
                  Get personalized guidance from our expert counselors for {program.title} programs.
                </p>
                <div className="space-y-3">
                  <Button
                    className="w-full bg-white text-ux-blue hover:bg-white/90"
                    asChild
                  >
                    <Link to="/contact" data-testid="book-consultation-btn">
                      <Calendar className="w-4 h-4 mr-2" />
                      {t.apply_now}
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-white/30 text-white hover:bg-white/10"
                    onClick={() => window.dispatchEvent(new CustomEvent('open-ai-chat'))}
                    data-testid="talk-ai-counselor-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    {detailFinalCta.cta_secondary_text || 'Talk to AI Counselor'}
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full text-white hover:bg-white/10"
                    asChild
                  >
                    <a href="tel:+919639557799">
                      <Phone className="w-4 h-4 mr-2" />
                      +91 9639 557 799
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Left Column - Main Content */}
              <div className="lg:col-span-2 space-y-8">
                {/* At a Glance stats grid (shown when the program provides it) */}
                {program.at_a_glance?.length > 0 && (
                  <Card
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                    data-testid="program-at-a-glance"
                  >
                    <CardHeader>
                      <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                        <TrendingUp className="w-5 h-5 text-ux-cyan" />
                        At a Glance
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {program.at_a_glance.map((stat, idx) => (
                          <div
                            key={idx}
                            className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}
                          >
                            <div className={`text-2xl font-bold mb-1 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`}>
                              {stat.label}
                            </div>
                            <div className={`text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                              {stat.detail}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Overview */}
                <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
                  <CardHeader>
                    <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                      <BookOpen className="w-5 h-5 text-ux-cyan" />
                      Program Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}>
                      {program.overview?.split('\n\n').map((para, idx) => (
                        <p key={idx} className={isDark ? 'text-white/80' : 'text-slate-600'}>
                          {para}
                        </p>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Why Choose This Program */}
                {program.why_choose?.length > 0 && (
                  <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
                    <CardHeader>
                      <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                        <Award className="w-5 h-5 text-ux-cyan" />
                        Why Choose {program.title.toLowerCase().includes(` in ${(program.destination || '').toLowerCase()}`)
                          ? program.title
                          : `${program.title} in ${program.destination}`}?
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {program.why_choose.map((point, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className={isDark ? 'text-white/80' : 'text-slate-600'}>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Course Duration & Curriculum */}
                {program.curriculum_phases?.length > 0 && (
                  <Card
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                    data-testid="program-curriculum-phases"
                  >
                    <CardHeader>
                      <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                        <BookOpen className="w-5 h-5 text-ux-cyan" />
                        Course Duration & Curriculum
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {program.curriculum_phases.map((p, idx) => (
                          <div
                            key={idx}
                            className={`flex gap-4 p-4 rounded-xl border ${
                              isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-100'
                            }`}
                          >
                            <div className={`flex-shrink-0 w-24 text-sm font-semibold ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`}>
                              {p.phase}
                            </div>
                            <div>
                              <div className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                {p.title}
                              </div>
                              <p className={`text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                                {p.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Eligibility */}
                <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
                  <CardHeader>
                    <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                      <FileText className="w-5 h-5 text-ux-cyan" />
                      {t.requirements_header}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          Educational Background
                        </h4>
                        <p className={isDark ? 'text-white/70' : 'text-slate-600'}>
                          {eligibility.education || 'Bachelor\'s degree in relevant field'}
                        </p>
                      </div>
                      <div>
                        <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          Minimum GPA
                        </h4>
                        <p className={isDark ? 'text-white/70' : 'text-slate-600'}>
                          {eligibility.minimum_gpa || '3.0/4.0 or equivalent'}
                        </p>
                      </div>
                      <div>
                        <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          English Proficiency
                        </h4>
                        <ul className={`space-y-1 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                          {(eligibility.english_tests || ['IELTS: 6.5+', 'TOEFL: 90+']).map((test, idx) => (
                            <li key={idx}>{test}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          Additional Tests
                        </h4>
                        {eligibility.additional_tests?.length > 0 ? (
                          <ul className={`space-y-1 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                            {eligibility.additional_tests.map((test, idx) => (
                              <li key={idx}>{test}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className={isDark ? 'text-white/70' : 'text-slate-600'}>
                            Varies by university
                          </p>
                        )}
                      </div>
                      {eligibility.work_experience && (
                        <div className="md:col-span-2">
                          <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            Work Experience
                          </h4>
                          <p className={isDark ? 'text-white/70' : 'text-slate-600'}>
                            {eligibility.work_experience}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Career Prospects */}
                <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
                  <CardHeader>
                    <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                      <Briefcase className="w-5 h-5 text-ux-cyan" />
                      Career Prospects
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      {careerProspects.job_roles?.length > 0 && (
                        <div>
                          <h4 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            Job Roles
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {careerProspects.job_roles.map((role, idx) => (
                              <Badge key={idx} variant="secondary" className={isDark ? 'bg-white/10 text-white/80' : ''}>
                                {role}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {careerProspects.average_salary && (
                        <div>
                          <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            Average Salary
                          </h4>
                          <p className={`text-2xl font-bold ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`}>
                            {careerProspects.average_salary}
                          </p>
                        </div>
                      )}
                      {careerProspects.top_employers?.length > 0 && (
                        <div>
                          <h4 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            Top Employers
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {careerProspects.top_employers.map((emp, idx) => (
                              <Badge key={idx} variant="outline" className={isDark ? 'border-white/30 text-white/80' : ''}>
                                <Building2 className="w-3 h-3 mr-1" />
                                {emp}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {careerProspects.growth_outlook && (
                        <div>
                          <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            Growth Outlook
                          </h4>
                          <p className={`flex items-center gap-2 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                            <TrendingUp className="w-4 h-4 text-green-500" />
                            {careerProspects.growth_outlook}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Licensing Pathways (country-wise) */}
                {program.licensing_pathways?.length > 0 && (
                  <Card
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                    data-testid="program-licensing-pathways"
                  >
                    <CardHeader>
                      <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                        <Globe className="w-5 h-5 text-ux-cyan" />
                        Where This Degree Lets You Practise
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                        A recognised degree doesn&apos;t mean you can practise anywhere immediately — every country has its own licensing exam.
                      </p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {program.licensing_pathways.map((row, idx) => (
                          <div
                            key={idx}
                            className={`p-4 rounded-xl border ${
                              isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-100'
                            }`}
                          >
                            <div className={`text-xs uppercase tracking-wider mb-1 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`}>
                              {row.country}
                            </div>
                            <p className={`text-sm ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
                              {row.exam}
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Living Costs breakdown */}
                {program.living_costs?.length > 0 && (
                  <Card
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                    data-testid="program-living-costs"
                  >
                    <CardHeader>
                      <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                        <DollarSign className="w-5 h-5 text-ux-cyan" />
                        Monthly Living Costs
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className={`overflow-hidden rounded-xl border ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
                        <table className="w-full text-sm">
                          <thead className={isDark ? 'bg-white/5' : 'bg-slate-50'}>
                            <tr>
                              <th className={`text-left px-4 py-3 font-semibold ${isDark ? 'text-white/80' : 'text-slate-700'}`}>Expense</th>
                              <th className={`text-right px-4 py-3 font-semibold ${isDark ? 'text-white/80' : 'text-slate-700'}`}>Approx. Monthly</th>
                            </tr>
                          </thead>
                          <tbody>
                            {program.living_costs.map((row, idx) => (
                              <tr key={idx} className={idx % 2 === 0 ? '' : (isDark ? 'bg-white/[0.02]' : 'bg-slate-50/50')}>
                                <td className={`px-4 py-3 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>{row.expense}</td>
                                <td className={`px-4 py-3 text-right font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{row.monthly}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Visa & Scholarships */}
                <div className="grid md:grid-cols-2 gap-6">
                  {program.visa_info && (
                    <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
                      <CardHeader>
                        <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : ''}`}>
                          <Plane className="w-5 h-5 text-ux-cyan" />
                          Visa & Application Process
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className={`whitespace-pre-line ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                          {program.visa_info}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                  {program.scholarships_info && (
                    <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
                      <CardHeader>
                        <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : ''}`}>
                          <Award className="w-5 h-5 text-ux-cyan" />
                          Scholarships
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className={isDark ? 'text-white/70' : 'text-slate-600'}>
                          {program.scholarships_info}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>

              {/* Right Column - Sidebar */}
              <div className="space-y-6">
                {/* MBBS-specific lead-capture form (only for MBBS / Medicine programs) */}
                {(program.field_of_study === 'Medicine' || (program.slug || '').startsWith('mbbs')) && (
                  <MBBSLeadForm
                    defaultCountry={program.destination || 'Russia'}
                    programSlug={program.slug}
                  />
                )}

                {/* Top Universities */}
                {program.top_universities?.length > 0 && (
                  <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
                    <CardHeader>
                      <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : ''}`}>
                        <GraduationCap className="w-5 h-5 text-ux-cyan" />
                        Top Universities
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {program.top_universities.map((uni, idx) => (
                          <li key={idx} className={`flex items-center gap-2 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              isDark ? 'bg-ux-cyan/20 text-ux-cyan' : 'bg-ux-blue/10 text-ux-blue'
                            }`}>
                              {idx + 1}
                            </div>
                            {uni}
                          </li>
                        ))}
                      </ul>
                      <Button
                        variant="outline"
                        className={`w-full mt-4 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}
                        asChild
                      >
                        <Link to="/universities">
                          View All Universities
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Quick Facts */}
                <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
                  <CardHeader>
                    <CardTitle className={`text-lg ${isDark ? 'text-white' : ''}`}>
                      Quick Facts
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <InfoRow label="Destination" value={program.destination} isDark={isDark} />
                    <InfoRow label="Degree" value={program.degree_level} isDark={isDark} />
                    <InfoRow label={t.duration_label} value={program.duration} isDark={isDark} />
                    <InfoRow label={t.intake_label} value={program.intake_seasons?.join(', ')} isDark={isDark} />
                  </CardContent>
                </Card>

                {/* Need Help CTA */}
                <Card className={`overflow-hidden ${isDark ? 'bg-gradient-to-br from-ux-blue/30 to-ux-cyan/30 border-white/20' : 'bg-gradient-to-br from-ux-blue to-ux-cyan border-0'}`}>
                  <CardContent className="p-6 text-center text-white">
                    <Users className="w-10 h-10 mx-auto mb-3 opacity-90" />
                    <h3 className="font-bold text-lg mb-2">Need Expert Guidance?</h3>
                    <p className="text-sm text-white/80 mb-4">
                      Our counselors have helped 30,000+ students achieve their study abroad dreams
                    </p>
                    <Button
                      className="w-full bg-white text-ux-blue hover:bg-white/90"
                      asChild
                    >
                      <Link to="/contact">
                        Get Free Counseling
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Related Programs */}
        {relatedPrograms.length > 0 && (
          <section className={`py-12 ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`} data-testid="related-programs-header">
                {t.related_header}
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedPrograms.map(prog => (
                  <Link
                    key={prog.id}
                    to={`/programs/${prog.slug}`}
                    className="group"
                  >
                    <Card className={`h-full transition-all duration-300 hover:shadow-lg ${
                      isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white hover:shadow-xl'
                    }`}>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="outline" className={isDark ? 'border-ux-cyan/50 text-ux-cyan' : ''}>
                            {prog.destination}
                          </Badge>
                          <Badge variant="secondary" className={isDark ? 'bg-white/10 text-white/70' : ''}>
                            {prog.degree_level}
                          </Badge>
                        </div>
                        <h3 className={`font-bold mb-2 group-hover:text-ux-cyan transition-colors ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {prog.title}
                        </h3>
                        <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                          {prog.duration} | {prog.avg_tuition}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default ProgramDetailPage;
